import { Fragment, useEffect, useMemo, useState } from 'react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Dropdown from '@/components/ui/Dropdown';
import Modal from '@/components/ui/Modal';
import Textarea from '@/components/ui/Textarea';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import type { CompetitionGuidance } from '@/api/guidanceService';
import { useCompetitionGuidance } from './useCompetitionGuidance';

const truncate = (value: string | null) => {
  if (!value) return '—';
  return value.length > 60 ? `${value.slice(0, 60)}…` : value;
};

const CompetitionGuidanceModal = ({
  guidance,
  onClose,
  onSave,
  isSaving,
}: {
  guidance: CompetitionGuidance | null;
  onClose: () => void;
  onSave: (payload: { coachNote: string; nutrition: string }) => Promise<unknown>;
  isSaving: boolean;
}) => {
  const [coachNote, setCoachNote] = useState('');
  const [nutrition, setNutrition] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (guidance) {
      setError('');
      setCoachNote(guidance.coachNote ?? '');
      setNutrition(guidance.nutrition ?? '');
    }
  }, [guidance]);

  const handleSubmit = async () => {
    try {
      await onSave({ coachNote: coachNote.trim(), nutrition: nutrition.trim() });
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={guidance !== null}
      onClose={onClose}
      size="lg"
      title={`Mot du coach compétition — ${guidance ? `${guidance.raceTypeLabel} ${guidance.formatLabel}` : ''}`}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Textarea label="Mot du coach" rows={6} value={coachNote} onChange={(e) => setCoachNote(e.target.value)} />
        <Textarea label="Nutrition" rows={4} value={nutrition} onChange={(e) => setNutrition(e.target.value)} />
        <p className="text-xs text-gray-500">Une ligne commençant par « * » s'affiche en puce. Seul le mot du coach est montré sur la case compétition.</p>
        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

/** One competition coach note per format, grouped by race type (Triathlon, Duathlon, Course à pied). */
const CompetitionGuidanceTab = () => {
  const { items, isLoading, saveGuidance, deleteGuidance, isSaving } = useCompetitionGuidance();
  const [editing, setEditing] = useState<CompetitionGuidance | null>(null);

  const groups = useMemo(() => {
    const byRaceType = new Map<number, { label: string; items: CompetitionGuidance[] }>();
    for (const item of items) {
      const group = byRaceType.get(item.raceTypeId) ?? { label: item.raceTypeLabel, items: [] };
      group.items.push(item);
      byRaceType.set(item.raceTypeId, group);
    }
    return [...byRaceType.entries()];
  }, [items]);

  const handleDelete = (guidance: CompetitionGuidance) => {
    if (window.confirm(`Supprimer le mot du coach compétition « ${guidance.raceTypeLabel} ${guidance.formatLabel} » ?`)) {
      deleteGuidance(guidance.formatId);
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500">Chargement…</p>;

  return (
    <Card className="p-0">
      <Table>
        <THead>
          <tr>
            <TH>Format</TH>
            <TH>Mot du coach</TH>
            <TH>Nutrition</TH>
            <TH className="text-right">Actions</TH>
          </tr>
        </THead>
        <TBody>
          {groups.map(([raceTypeId, group]) => (
            <Fragment key={raceTypeId}>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <TD colSpan={4} className="py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {group.label}
                </TD>
              </tr>
              {group.items.map((guidance) => (
                <TR key={guidance.formatId}>
                  <TD className="font-bold text-gray-900 dark:text-gray-100">{guidance.formatSlug}</TD>
                  <TD className="text-gray-500" title={guidance.coachNote ?? ''}>
                    {truncate(guidance.coachNote)}
                  </TD>
                  <TD className="text-gray-500" title={guidance.nutrition ?? ''}>
                    {truncate(guidance.nutrition)}
                  </TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Modifier',
                          icon: <PencilSquareIcon className="h-4 w-4" />,
                          onClick: () => setEditing(guidance),
                        },
                        ...(guidance.updatedAt
                          ? [
                              {
                                label: 'Supprimer',
                                icon: <TrashIcon className="h-4 w-4" />,
                                destructive: true,
                                onClick: () => handleDelete(guidance),
                              },
                            ]
                          : []),
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </Fragment>
          ))}
        </TBody>
      </Table>

      <CompetitionGuidanceModal
        guidance={editing}
        onClose={() => setEditing(null)}
        isSaving={isSaving}
        onSave={(payload) => saveGuidance({ formatId: editing!.formatId, payload })}
      />
    </Card>
  );
};

export default CompetitionGuidanceTab;
